import { useState, useEffect, useCallback } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getFirestorePath } from '../utils/vaultUtils';
import { hasStorageAccess } from '../utils/accessControl';
import logger from '../utils/logger';

export const useVaultItems = (params) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize the query parameters to prevent unnecessary re-renders
  const queryParams = useCallback(() => {
    if (!params || !params.userId || !params.collectionType || !params.storageArea) {
      return null;
    }
    return {
      userId: params.userId,
      familyCode: params.familyCode,
      collectionType: params.collectionType,
      storageArea: params.storageArea
    };
  }, [params]);

  useEffect(() => {
    let isSubscribed = true;
    let unsubscribe = null;

    const setupSubscription = async () => {
      try {
        if (isSubscribed) {
          setLoading(true);
          setError(null);
        }

        const currentParams = queryParams();
        if (!currentParams) {
          if (isSubscribed) {
            setItems([]);
            setLoading(false);
          }
          return;
        }

        const { userId, familyCode, collectionType, storageArea } = currentParams;

        // Add additional validation for required fields
        if (!userId) {
          throw new Error('User ID is required');
        }

        if (!collectionType) {
          throw new Error('Collection type is required');
        }

        if (!storageArea) {
          throw new Error('Storage area is required');
        }

        // For family or vault access, ensure we have a family code
        if ((storageArea === 'family' || storageArea === 'vault') && !familyCode) {
          if (isSubscribed) {
            setError('Family code is required for family or vault access');
            setItems([]);
            setLoading(false);
          }
          return;
        }

        // Check access permissions with more detailed error handling
        try {
          const hasAccess = await hasStorageAccess({ uid: userId }, familyCode, storageArea);
          if (!hasAccess) {
            if (isSubscribed) {
              setError(`You do not have permission to access the ${storageArea} storage area`);
              setItems([]);
              setLoading(false);
            }
            return;
          }
        } catch (accessError) {
          logger.error('Error checking storage access:', accessError);
          if (isSubscribed) {
            setError('Failed to verify storage access permissions');
            setItems([]);
            setLoading(false);
          }
          return;
        }

        // Get Firestore path with error handling
        let path;
        try {
          path = getFirestorePath(userId, familyCode, collectionType, storageArea);
          if (!path) {
            throw new Error('Invalid path configuration');
          }
        } catch (pathError) {
          logger.error('Error getting Firestore path:', pathError);
          if (isSubscribed) {
            setError('Failed to construct valid database path');
            setItems([]);
            setLoading(false);
          }
          return;
        }

        // Set up Firestore subscription with error boundaries
        try {
          const q = query(collection(db, path));
          unsubscribe = onSnapshot(q, 
            (snapshot) => {
              if (isSubscribed) {
                const fetchedItems = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                setItems(fetchedItems);
                setLoading(false);
                setError(null);
              }
            },
            (error) => {
              logger.error('Error in Firestore subscription:', error);
              if (isSubscribed) {
                setError(error.message);
                setItems([]);
                setLoading(false);
              }
            }
          );
        } catch (subscriptionError) {
          logger.error('Error setting up Firestore subscription:', subscriptionError);
          if (isSubscribed) {
            setError('Failed to set up file monitoring');
            setItems([]);
            setLoading(false);
          }
        }
      } catch (error) {
        logger.error('Unexpected error in useVaultItems:', error);
        if (isSubscribed) {
          setError(error.message || 'An unexpected error occurred');
          setItems([]);
          setLoading(false);
        }
      }
    };

    setupSubscription();

    return () => {
      isSubscribed = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [queryParams]);

  const refresh = useCallback(() => {
    // For refresh, we just rely on the Firestore real-time updates
    return Promise.resolve();
  }, []);

  return { items, loading, error, refresh };
}; 